import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Checkbox,
  Avatar,
  IconButton,
  Slide,
  DialogTitle,
  Box,
} from "@mui/material";
import {
  Close as CloseIcon,
  MailOutline as MailOutlineIcon,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { registrationApi, otpSignIn } from "../../layouts/authentication/_request";
import { useAuth } from "context";
import { useOtp } from "context/OtpContext";
import SimpleBackdrop from "components/MDLoader_Backdrop";
import { AnalyticsEvents } from "core/constant";
import { useAnalyticsEvent } from "hooks/useAnalyticsEvent";
import OtpInputForm from "layouts/restaurant/components/OtpSignInModal/OtpInputForm";
import SignInWithGoogle from "layouts/restaurant/components/OtpSignInModal/SignInWithGoogle";
import CampaignTargetingModal, {
  isCampaignTargetingError,
} from "components/custom/CampaignTargetingModal";
import { OTP_LOGIN_ACTION, AUTH_ERROR_CODES } from "constants/authConstants";
import PropTypes from "prop-types";
import crLogo from "../../assets/images/icons/catering-rewards/cr-logo-black.png";
import TurnstileWidget from "components/TurnstileWidget";
import config from "config/config";

const envConfig = config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"];

const OTP_LENGTH = 6;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const GhlSignUpModal = ({
  isOpen,
  onClose,
  gtmCode,
  campaignCode,
  landingData,
  campaignDetails,
  referrerCode,
}) => {
  const { saveAuthData, showSnackbarWithMessage } = useAuth();
  const { dispatch } = useOtp();
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [isExistingUserFlow, setIsExistingUserFlow] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [nonMarketingConsent, setNonMarketingConsent] = useState(false);
  const [level, setLevel] = useState(0);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [loader, setLoader] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [targetingModal, setTargetingModal] = useState({ open: false, errorCode: null });
  const [captchaToken, setCaptchaToken] = useState(null);
  const turnstileRef = useRef(null);
  const resetCaptcha = useCallback(() => {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  }, []);

  const restaurantName = landingData?.name;

  const [registerData, setRegisterData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    workEmail: "",
    password: "",
    phoneNumber: "",
    role: "CUSTOMER",
  });

  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const { trackCustomEvent } = useAnalyticsEvent();

  const phonePattern = /^\(\d{3}\) \d{3}-\d{4}$/;

  const isEmailValid = (email) => {
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailPattern.test(email);
  };

  useEffect(() => {
    if (!isOpen) {
      setShowOtpForm(false);
      setRegisterData({
        firstName: "",
        lastName: "",
        email: "",
        workEmail: "",
        password: "",
        phoneNumber: "",
        role: "CUSTOMER",
      });
      setMarketingConsent(false);
      setNonMarketingConsent(false);
      setNameError("");
      setEmailError("");
      setPhoneError("");
      setConsentError(false);
      setLevel(0);
      setTargetingModal({ open: false, errorCode: null });
      setIsExistingUserFlow(false);
      setCaptchaToken(null);
      dispatch({ type: OTP_LOGIN_ACTION.RESET });
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (level === 1) {
      onClose();
    }
  }, [level, onClose]);

  const handleMarketingConsentChange = (event) => {
    setMarketingConsent(event.target.checked);
    if (event.target.checked && nonMarketingConsent) {
      setConsentError(false);
    }
  };

  const handleNonMarketingConsentChange = (event) => {
    setNonMarketingConsent(event.target.checked);
    if (event.target.checked && marketingConsent) {
      setConsentError(false);
    }
  };

  const formatPhoneNumber = (value) => {
    value = value.replace(/\D/g, "");
    if (value.length > 10) value = value.slice(0, 10);

    if (!value || value.length === 0) return "";
    if (value.length <= 3) return `(${value}`;
    if (value.length <= 6) return `(${value.slice(0, 3)}) ${value.slice(3)}`;
    return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
  };

  const handlePhoneChange = (event) => {
    const formattedValue = formatPhoneNumber(event.target.value);
    setRegisterData((prevData) => ({
      ...prevData,
      phoneNumber: formattedValue,
    }));

    if (formattedValue && !phonePattern.test(formattedValue)) {
      setPhoneError("Phone number must be in the format (XXX) XXX-XXXX");
    } else {
      setPhoneError("");
    }
  };

  const handleFormFieldChange = (event) => {
    const { name, value } = event.target;
    setRegisterData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (name === "firstName") {
      if (value.trim().length < 3) {
        setNameError("Full name should be at least 3 characters.");
      } else {
        setNameError("");
      }
    } else if (name === "email") {
      if (value && !isEmailValid(value)) {
        setEmailError("Email is not valid.");
      } else {
        setEmailError("");
      }
    }
  };

  useEffect(() => {
    updateButtonDisabledState();
  }, [marketingConsent, nonMarketingConsent, registerData]);

  const updateButtonDisabledState = () => {
    const shouldDisableButton =
      !marketingConsent ||
      !nonMarketingConsent ||
      registerData.firstName.trim() === "" ||
      registerData.email.trim() === "" ||
      !isEmailValid(registerData.email);
    setButtonDisabled(shouldDisableButton);
  };

  const checkConsentBeforeSignIn = () => {
    if (!marketingConsent || !nonMarketingConsent) {
      setConsentError(true);
      showSnackbarWithMessage({
        type: "error",
        message: "Please accept both consent agreements to continue.",
      });
      return false;
    }
    setConsentError(false);
    return true;
  };

  const closeTargetingModal = () => setTargetingModal({ open: false, errorCode: null });

  const buildNameAndPassword = () => {
    const nameParts = registerData.firstName.trim().split(" ");
    return {
      firstName: nameParts[0] || "",
      lastName: nameParts.slice(1).join(" ") || registerData.lastName || "",
      password: Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-8),
    };
  };

  const finalizeSignup = async ({ data, message, analyticsCampaignCode }) => {
    const verifiedCustomer = await saveAuthData(data.tokens);
    trackCustomEvent(
      AnalyticsEvents.SIGNUP,
      {
        method: "password",
        auth_origin: "landing_portal",
        user_id: verifiedCustomer.id,
        user_role: verifiedCustomer.role,
        user_type: verifiedCustomer.userType,
        restaurantStoreId: landingData?.storeId,
        campaignCode: analyticsCampaignCode,
      },
      false
    );
    closeTargetingModal();
    setShowOtpForm(true);
    showSnackbarWithMessage({ type: "success", message });
  };

  const showRegistrationError = (message = "Registration failed. Please try again.") => {
    closeTargetingModal();
    showSnackbarWithMessage({ type: "error", message });
  };

  const handleSubmit = async () => {
    try {
      setLoader(true);
      setIsExistingUserFlow(false);
      const {
        data: { success, message, data, errorCode },
      } = await registrationApi(
        {
          ...registerData,
          ...buildNameAndPassword(),
          ...(!campaignDetails && !referrerCode && { origin: "LANDING" }),
          restaurantStoreId: landingData?.storeId,
          campaignCode: campaignDetails ? campaignCode : null,
          referrerCode,
        },
        campaignCode
      );

      if (!success && errorCode === AUTH_ERROR_CODES.EXISTING_CUSTOMER_SIGN_IN_REQUIRED) {
        const otpResponse = await otpSignIn({ identifier: registerData.email, captchaToken });
        resetCaptcha();
        const otpSent = otpResponse && otpResponse.data && otpResponse.data.success;
        const otpMessage = otpResponse && otpResponse.data && otpResponse.data.message;

        if (otpResponse && otpResponse.status === 403) {
          showSnackbarWithMessage({
            type: "error",
            message: "Verification failed, please try again.",
          });
          return;
        }

        if (!otpSent) {
          showSnackbarWithMessage({
            type: "error",
            message: otpMessage || "Could not send verification code. Please try again.",
          });
          return;
        }

        dispatch({ type: OTP_LOGIN_ACTION.SET_IDENTIFIER, payload: registerData.email });
        setIsExistingUserFlow(true);
        setShowOtpForm(true);
        return;
      }

      if (!success && isCampaignTargetingError(errorCode)) {
        setTargetingModal({ open: true, errorCode });
        return;
      }

      if (success) {
        await finalizeSignup({
          data,
          message,
          analyticsCampaignCode: campaignDetails ? campaignCode : null,
        });
        return;
      }

      showRegistrationError(message);
    } catch (error) {
      showRegistrationError();
    } finally {
      setLoader(false);
    }
  };

  const handleTargetingContinue = async () => {
    try {
      setLoader(true);
      setIsExistingUserFlow(false);
      const {
        data: { success, message, data },
      } = await registrationApi({
        ...registerData,
        ...buildNameAndPassword(),
        origin: "LANDING",
        restaurantStoreId: landingData?.storeId,
        campaignCode: null,
      });

      if (success) {
        await finalizeSignup({ data, message, analyticsCampaignCode: null });
        return;
      }

      showRegistrationError(message);
    } catch (error) {
      showRegistrationError();
    } finally {
      setLoader(false);
    }
  };

  const handleClose = () => {
    if (!loader) {
      onClose();
    }
  };

  const handleBackToSignup = () => {
    setShowOtpForm(false);
    setIsExistingUserFlow(false);
    dispatch({ type: OTP_LOGIN_ACTION.RESET });
  };

  return (
    <>
      <Dialog
        open={isOpen && !targetingModal.open}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="xs"
        fullWidth
        aria-labelledby={showOtpForm ? "ghl-otp-title" : "ghl-signup-title"}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            margin: 2,
            maxHeight: "90vh",
          },
        }}
      >
        {!showOtpForm && (
          <DialogTitle sx={{ p: 0, textAlign: "center" }}>
            <Box mt={1.5}>
              <img
                src={crLogo}
                alt="Logo"
                className="mx-auto"
                style={{ width: 120, height: "auto" }}
              />
            </Box>

            <MDTypography
              id="ghl-signup-title"
              variant="h2"
              sx={{
                fontSize: { xs: "1.125rem", md: "1.25rem" },
                fontWeight: 700,
                color: "#1A202C",
                mt: 1,
                textTransform: "none",
                letterSpacing: "-0.02em",
              }}
            >
              Welcome to Catering Rewards!
            </MDTypography>

            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: "absolute",
                right: 12,
                top: 12,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
        )}
        <DialogContent sx={{ padding: 0 }}>
          {showOtpForm ? (
            <MDBox p={2} textAlign="center">
              <MDBox mb={2} mt={1} display="flex" justifyContent="center" alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "#CEFFE1",
                    width: 40,
                    height: 40,
                  }}
                >
                  <MailOutlineIcon sx={{ fontSize: 20, color: "rgb(16, 121, 72)" }} />
                </Avatar>
              </MDBox>

              <MDTypography
                id="ghl-otp-title"
                sx={{ color: "#101828", fontSize: "24px" }}
                variant="h4"
                fontWeight="medium"
                mb={5}
              >
                Please check your email
              </MDTypography>

              {isExistingUserFlow ? (
                <OtpInputForm
                  gtmCode={gtmCode}
                  setLevel={setLevel}
                  length={OTP_LENGTH}
                  setLoader={setLoader}
                  campaignCode={campaignCode}
                  campaignDetails={campaignDetails}
                  storeId={landingData?.storeId}
                  brandId={landingData?.brandId}
                  handleBack={handleBackToSignup}
                />
              ) : (
                <OtpInputForm
                  gtmCode={gtmCode}
                  setLevel={setLevel}
                  length={OTP_LENGTH}
                  ghlEmail={registerData.email}
                  isGhl={true}
                  setLoader={setLoader}
                  campaignCode={campaignCode}
                  campaignDetails={campaignDetails}
                  handleBack={handleBackToSignup}
                />
              )}
            </MDBox>
          ) : (
            <MDBox p={3} pt={1}>
              <MDBox
                component="form"
                role="form"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!buttonDisabled && !loader) handleSubmit();
                }}
              >
                {/* Login with Google Button */}
                <SignInWithGoogle
                  gtmCode={gtmCode}
                  campaignCode={campaignCode}
                  campaignDetails={campaignDetails}
                  storeId={landingData?.storeId}
                  brandId={landingData?.brandId}
                  onBeforeSignIn={checkConsentBeforeSignIn}
                />

                {/* OR Divider */}
                <MDBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                    mt: 2,
                    mb: 2,
                  }}
                >
                  <MDBox
                    sx={{
                      height: "1px",
                      flex: 1,
                      background: "#AFAFAF",
                    }}
                  />
                  <MDTypography
                    variant="button"
                    sx={{
                      color: "#AFAFAF",
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                    }}
                  >
                    or
                  </MDTypography>
                  <MDBox
                    sx={{
                      height: "1px",
                      flex: 1,
                      background: "#AFAFAF",
                    }}
                  />
                </MDBox>

                <MDBox mb={1.25}>
                  <MDTypography
                    variant="body2"
                    fontWeight="medium"
                    color="dark"
                    mb={0.5}
                    sx={{ fontSize: "0.8125rem" }}
                  >
                    Full Name <span style={{ color: "red" }}>*</span>
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder="Enter your name"
                    name="firstName"
                    fullWidth
                    value={registerData.firstName}
                    onChange={handleFormFieldChange}
                    error={!!nameError}
                    helperText={nameError}
                    FormHelperTextProps={{
                      sx: {
                        color: "#d32f2f",
                        marginLeft: 0,
                        fontSize: "0.75rem",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a9a9a9",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#808080",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "10px 14px",
                      },
                    }}
                  />
                </MDBox>

                <MDBox mb={1.25}>
                  <MDTypography
                    variant="body2"
                    fontWeight="medium"
                    color="dark"
                    mb={0.5}
                    sx={{ fontSize: "0.8125rem" }}
                  >
                    Email <span style={{ color: "red" }}>*</span>
                  </MDTypography>
                  <MDInput
                    type="email"
                    placeholder="Enter your email"
                    name="email"
                    fullWidth
                    value={registerData.email}
                    onChange={handleFormFieldChange}
                    error={!!emailError}
                    helperText={emailError}
                    FormHelperTextProps={{
                      sx: {
                        color: "#d32f2f",
                        marginLeft: 0,
                        fontSize: "0.75rem",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a9a9a9",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#808080",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "10px 14px",
                      },
                    }}
                  />
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="body2"
                    fontWeight="medium"
                    color="dark"
                    mb={0.5}
                    sx={{ fontSize: "0.8125rem" }}
                  >
                    Phone Number
                  </MDTypography>
                  <MDInput
                    type="tel"
                    placeholder="Enter your phone"
                    name="phoneNumber"
                    fullWidth
                    value={registerData.phoneNumber}
                    onChange={handlePhoneChange}
                    error={!!phoneError}
                    helperText={phoneError}
                    FormHelperTextProps={{
                      sx: {
                        color: "#d32f2f",
                        marginLeft: 0,
                        fontSize: "0.75rem",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#a9a9a9",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#808080",
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        padding: "10px 14px",
                      },
                    }}
                  />
                </MDBox>

                <MDBox display="flex" alignItems="flex-start" mb={1}>
                  <Checkbox
                    checked={marketingConsent}
                    onChange={handleMarketingConsentChange}
                    sx={{
                      mt: "4px",
                      padding: "0px 6px 0px 0",
                      color: consentError && !marketingConsent ? "#d32f2f" : "#414651",
                      "&.Mui-checked": {
                        color: "#4caf50",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "1.25rem",
                        border:
                          consentError && !marketingConsent
                            ? "1px solid #d32f2f"
                            : "1px solid rgb(65, 70, 81, .35)",
                        borderRadius: "4px",
                      },
                    }}
                  />
                  <p
                    style={{
                      fontSize: 12,
                      maxWidth: "360px",
                      color: consentError && !marketingConsent ? "#d32f2f" : "#414651",
                      lineHeight: "20px",
                      marginBottom: "10px",
                    }}
                  >
                    I consent to receive marketing text messages from{" "}
                    {restaurantName || "(Business Name)"} at the phone number provided. Frequency
                    may vary. Message & data rates may apply. Text HELP for assistance, reply STOP
                    to opt out.
                  </p>
                </MDBox>

                <MDBox display="flex" alignItems="flex-start" mb={1}>
                  <Checkbox
                    checked={nonMarketingConsent}
                    onChange={handleNonMarketingConsentChange}
                    sx={{
                      mt: "4px",
                      padding: "0px 6px 0px 0",
                      color: consentError && !nonMarketingConsent ? "#d32f2f" : "#414651",
                      "&.Mui-checked": {
                        color: "#4caf50",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "1.25rem",
                        border:
                          consentError && !marketingConsent
                            ? "1px solid #d32f2f"
                            : "1px solid rgb(65, 70, 81, .35)",
                        borderRadius: "4px",
                      },
                    }}
                  />
                  <p
                    style={{
                      fontSize: 12,
                      maxWidth: "360px",
                      color: consentError && !nonMarketingConsent ? "#d32f2f" : "#414651",
                      lineHeight: "20px",
                      marginBottom: "10px",
                    }}
                  >
                    I consent to receive non-marketing text messages from{" "}
                    {restaurantName || "(Business Name)"} about my order updates, appointment
                    reminders etc. Message & data rates may apply.
                  </p>
                </MDBox>

                <MDBox mb={1.5} textAlign="center">
                  <MDTypography
                    component="a"
                    href="https://cateringrewards.com/terms_of_use"
                    target="_blank"
                    variant="body2"
                    color="success"
                    fontWeight="medium"
                    sx={{ textDecoration: "none", fontSize: "0.75rem" }}
                  >
                    Terms of Service
                  </MDTypography>
                  <MDTypography
                    variant="body2"
                    color="text"
                    component="span"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    {" & "}
                  </MDTypography>
                  <MDTypography
                    component="a"
                    href="https://cateringrewards.com/privacy_policy"
                    target="_blank"
                    variant="body2"
                    color="success"
                    fontWeight="medium"
                    sx={{ textDecoration: "none", fontSize: "0.75rem" }}
                  >
                    Privacy Policy
                  </MDTypography>
                </MDBox>

                <TurnstileWidget
                  ref={turnstileRef}
                  siteKey={envConfig.TURNSTILE_SITE_KEY}
                  onToken={setCaptchaToken}
                  onExpire={() => setCaptchaToken(null)}
                  onError={() => setCaptchaToken(null)}
                />

                <MDBox>
                  <MDButton
                    variant="gradient"
                    color="success"
                    fullWidth
                    disabled={buttonDisabled || loader || !captchaToken}
                    onClick={handleSubmit}
                    sx={{
                      borderRadius: "8px",
                      padding: "9px",
                      fontSize: "0.9375rem",
                      fontWeight: "medium",
                      textTransform: "none",
                    }}
                  >
                    {loader ? "Loading..." : "Continue"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          )}
        </DialogContent>
      </Dialog>

      <CampaignTargetingModal
        open={targetingModal.open}
        onClose={() => setTargetingModal({ open: false, errorCode: null })}
        errorCode={targetingModal.errorCode}
        onContinue={handleTargetingContinue}
      />

      {loader && <SimpleBackdrop />}
    </>
  );
};

GhlSignUpModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  gtmCode: PropTypes.string,
  campaignCode: PropTypes.string,
  landingData: PropTypes.object,
  campaignDetails: PropTypes.object,
  referrerCode: PropTypes.string,
};

export default GhlSignUpModal;
